import type { Persister } from '@tanstack/query-persist-client-core'
import { isTauri } from '@/lib/utils/tauri'

import { createIndexedDBPersister } from './idb-persister'
import { createSQLitePersister } from './sqlite-persister'
import { createSerializedThrottledPersister } from './throttled-persister'

const PERSIST_THROTTLE_MS = 1500

export async function createPersister(
  scopeKey: string,
): Promise<Persister | null> {
  // VITE_BUILD_ID includes version + build timestamp, so every deploy busts cache.
  // Falls back to version-only if build ID is missing (dev mode).
  // scopeKey is `${userId}:${orgId}` — query keys are org-agnostic (the org
  // travels as a request header), so the cache itself must be org-scoped or a
  // restore after an org switch serves the previous org's rows under
  // identical query keys for up to the freshness window.
  const buildId =
    import.meta.env.VITE_BUILD_ID || import.meta.env.VITE_APP_VERSION || '0.0.0'
  const cacheKey = `scrambl-query-cache-${scopeKey}-${buildId}`

  try {
    if (isTauri()) {
      return createSerializedThrottledPersister(
        await createSQLitePersister(cacheKey),
        PERSIST_THROTTLE_MS,
      )
    }
    return createSerializedThrottledPersister(
      createIndexedDBPersister(cacheKey),
      PERSIST_THROTTLE_MS,
    )
  } catch (e) {
    console.warn(
      '[QueryCache] Failed to create persister, running without cache',
      e,
    )
    return null
  }
}
